import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getUserService } from '@src/container';
import type { UserDTO } from '@src/application/users/dto/user.dto';

interface UploadProfilePictureVariables {
    file: File;
    onUploadProgress?: (progress: number) => void;
}

/**
 * Hook: useUploadProfilePicture
 * Uploads a profile picture for the authenticated user
 * 
 * @param options - React Query mutation options
 * @returns Mutation result for uploading profile picture
 * 
 * @example
 * ```typescript
 * const { mutate: uploadPicture, isPending, error } = useUploadProfilePicture({
 *     onSuccess: (updatedUser) => {
 *         console.log('Profile picture updated:', updatedUser);
 *     }
 * });
 * 
 * // Usage
 * uploadPicture({
 *     file: imageFile,
 *     onUploadProgress: (progress) => setProgress(progress)
 * });
 * ```
 */
export function useUploadProfilePicture(
    options?: Omit<
        UseMutationOptions<UserDTO, Error, UploadProfilePictureVariables>,
        'mutationFn'
    >
) {
    const userService = getUserService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async ({ file, onUploadProgress }: UploadProfilePictureVariables) => {
            return await userService.uploadProfilePicture(file, onUploadProgress);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Refetch the current user query to get the User entity (not DTO)
            // This ensures the user object has all the methods like isSuperAdmin()
            await queryClient.refetchQueries({ queryKey: ['auth', 'currentUser'] });

            // Invalidate users list in case admin is viewing it
            await queryClient.invalidateQueries({ queryKey: ['users'] });

            // Call user's onSuccess callback if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        },
    });
}
